import type {
  IDropPointLab,
  IDropPointPrivate,
} from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAppStore } from '@/stores/appStore'
import { useAccountStore } from '@/stores/accountStore'
import { DropPoint } from '@/globals/typescript/models/DropPoint'
import { defineStore } from 'pinia'
import type { ICustomerRef } from '@team-milva/lab-models/customer'

export { DropPoint }

type DropPointParams = {
  name: string
  address: string
  postalCode: string
  city: string
  placement: string
  phone: IDropPointLab['phone']
  openingHours: IDropPointLab['openingHours']
  walkInTatIDs: string[]
  walkInWindow: IDropPointLab['walkInWindow']
  activeState?: 'active' | 'inactive'
  isSelfSendTarget?: boolean
}

type PrivateDropPointParams = {
  /** The business or insurance customer the drop point belongs to (MIL-402). */
  customer: ICustomerRef
  address: string
  postalCode: string
  city: string
  placement: string
  activeState: 'active' | 'inactive'
}

type UpdatePrivateDropPointParams = {
  address: string
  postalCode: string
  city: string
  placement: string
}

/**
 * The labManagement private-drop-point routes are mounted twice (MIL-402): under
 * `customer-accounts/{id}` and `insurance-customer-accounts/{id}`. A private customer has no
 * private drop points of its own — calling with one is a programming error.
 */
function assertCompanyCustomer(customer: ICustomerRef): void {
  if (customer.type !== 'business' && customer.type !== 'insurance') {
    throw new Error('invalid-params')
  }
}

type DhlCutoffCapability = {
  localCutoffDateAndTime: string | null
  price: number | null
  priceCurrency: string | null
}

export const useDropPointStore = defineStore('dropPoint', {
  state: () => ({
    labDropPoints: [] as DropPoint[],
    privateDropPoints: [] as DropPoint[],
    unsubscribeAllDropPoints: null as (() => void) | null,
    unsubscribePrivateDropPoints: null as (() => void) | null,
    // Cached for the session — DHL cutoff capability per private drop point rarely
    // changes within a session, so once fetched it is never re-requested.
    dhlCutoffCapabilityByDropPointID: {} as Record<
      string,
      DhlCutoffCapability | null | 'loading'
    >,
    dhlCutoffFetchPromiseByDropPointID: {} as Record<string, Promise<void>>,
  }),

  getters: {
    allDropPointsLoaded(): boolean {
      return (
        !!this.unsubscribeAllDropPoints && !!this.unsubscribePrivateDropPoints
      )
    },
  },

  actions: {
    getDropPoints(): Promise<DropPoint[]> {
      if (this.allDropPointsLoaded) {
        return Promise.resolve([
          ...this.labDropPoints,
          ...this.privateDropPoints,
        ])
      }

      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const labDropPointsPromise = new Promise<void>((resolve) => {
        let resolved = false
        const q = query(
          collection(db, 'Apps', '-lab-management-', 'LabDropPoints'),
          where('labAccountID', '==', labAccountID)
        )
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.labDropPoints = querySnapshot.docs
            .map((doc) => new DropPoint(doc.data() as IDropPointLab))
            .sort((a, b) => a.timestamps.createdAt - b.timestamps.createdAt)
          this.unsubscribeAllDropPoints = unsubscribe
          if (!resolved) {
            resolved = true
            resolve()
          }
        })
      })

      const privateDropPointsPromise = new Promise<void>((resolve) => {
        let resolved = false
        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'PrivateDropPoints'),
          where('labAccountID', '==', labAccountID)
        )
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.privateDropPoints = querySnapshot.docs
            .map((doc) => new DropPoint(doc.data() as IDropPointPrivate))
            .sort((a, b) => a.timestamps.createdAt - b.timestamps.createdAt)
          this.unsubscribePrivateDropPoints = unsubscribe
          if (!resolved) {
            resolved = true
            resolve()
          }
        })
      })

      return Promise.all([labDropPointsPromise, privateDropPointsPromise]).then(
        () => [...this.labDropPoints, ...this.privateDropPoints]
      )
    },

    async createDropPoint(params: DropPointParams): Promise<void> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()

      const result = await managementApi('post', 'lab-accounts/{labAccountID}/drop-points', {
        path: { labAccountID: accountStore.labAccount?.id ?? '' },
        body: {
          name: { [currentLocale]: params.name },
          placement: { [currentLocale]: params.placement },
          address: params.address,
          postalCode: params.postalCode,
          city: params.city,
          phone: params.phone,
          openingHours: params.openingHours,
          walkInTatIDs: params.walkInTatIDs,
          walkInWindow: params.walkInWindow,
          activeState: params.activeState ?? 'active',
          isSelfSendTarget: params.isSelfSendTarget,
        },
      })
      if (!result.success) throw new Error(result.code)
    },

    async updateDropPoint(id: string, params: DropPointParams): Promise<void> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/drop-points/{dropPointID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            dropPointID: id,
          },
          body: {
            name: { [currentLocale]: params.name },
            placement: { [currentLocale]: params.placement },
            address: params.address,
            postalCode: params.postalCode,
            city: params.city,
            phone: params.phone,
            openingHours: params.openingHours,
            walkInTatIDs: params.walkInTatIDs,
            walkInWindow: params.walkInWindow,
            activeState: params.activeState,
            isSelfSendTarget: params.isSelfSendTarget,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async deactivateDropPoint(id: string): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/drop-points/{dropPointID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            dropPointID: id,
          },
          body: { activeState: 'inactive' },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async reactivateDropPoint(id: string): Promise<void> {
      const accountStore = useAccountStore()
      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/drop-points/{dropPointID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            dropPointID: id,
          },
          body: { activeState: 'active' },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async createPrivateDropPoint(
      params: PrivateDropPointParams
    ): Promise<void> {
      const { customer } = params
      assertCompanyCustomer(customer)
      const { currentLocale } = useAppStore()
      const labAccountID = useAccountStore().labAccount?.id ?? ''
      const body = {
        address: params.address,
        postalCode: params.postalCode,
        city: params.city,
        placement: { [currentLocale]: params.placement },
        activeState: params.activeState,
      }

      const result =
        customer.type === 'insurance'
          ? await managementApi(
              'post',
              'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/private-drop-points',
              {
                path: { labAccountID, insuranceCustomerAccountID: customer.id },
                body,
              }
            )
          : await managementApi(
              'post',
              'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/private-drop-points',
              { path: { labAccountID, customerAccountID: customer.id }, body }
            )
      if (!result.success) throw new Error(result.code)
    },

    async updatePrivateDropPoint(
      id: string,
      customer: ICustomerRef,
      params: UpdatePrivateDropPointParams
    ): Promise<void> {
      const { currentLocale } = useAppStore()
      await this.patchPrivateDropPoint(id, customer, {
        address: params.address,
        postalCode: params.postalCode,
        city: params.city,
        placement: { [currentLocale]: params.placement },
      })
    },

    async deactivatePrivateDropPoint(
      id: string,
      customer: ICustomerRef
    ): Promise<void> {
      await this.patchPrivateDropPoint(id, customer, { activeState: 'inactive' })
    },

    async reactivatePrivateDropPoint(
      id: string,
      customer: ICustomerRef
    ): Promise<void> {
      await this.patchPrivateDropPoint(id, customer, { activeState: 'active' })
    },

    /** The one PATCH behind update/deactivate/reactivate, on the owner's own route. */
    async patchPrivateDropPoint(
      id: string,
      customer: ICustomerRef,
      body: {
        address?: string
        postalCode?: string
        city?: string
        placement?: Record<string, string>
        activeState?: 'active' | 'inactive'
      }
    ): Promise<void> {
      assertCompanyCustomer(customer)
      const labAccountID = useAccountStore().labAccount?.id ?? ''

      const result =
        customer.type === 'insurance'
          ? await managementApi(
              'patch',
              'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/private-drop-points/{privateDropPointID}',
              {
                path: {
                  labAccountID,
                  insuranceCustomerAccountID: customer.id,
                  privateDropPointID: id,
                },
                body,
              }
            )
          : await managementApi(
              'patch',
              'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/private-drop-points/{privateDropPointID}',
              {
                path: {
                  labAccountID,
                  customerAccountID: customer.id,
                  privateDropPointID: id,
                },
                body,
              }
            )
      if (!result.success) throw new Error(result.code)
    },

    // Joins any already in-flight (or already resolved) fetch per drop point ID,
    // so calling this repeatedly with overlapping ID lists never re-requests an ID
    // that a previous caller already kicked off or completed this session.
    fetchDhlCutoffCapability(dropPointIDs: string[]): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) return Promise.resolve()

      const promises = dropPointIDs.map((dropPointID) => {
        const existing = this.dhlCutoffFetchPromiseByDropPointID[dropPointID]
        if (existing) return existing

        this.dhlCutoffCapabilityByDropPointID[dropPointID] = 'loading'
        const promise = (async () => {
          try {
            const result = await managementApi(
              'get',
              '/lab-accounts/{labAccountID}/integrations/dhl/drop-point-capability/{dropPointID}',
              { path: { labAccountID, dropPointID } }
            )
            this.dhlCutoffCapabilityByDropPointID[dropPointID] = result.success
              ? {
                  localCutoffDateAndTime:
                    result.data.localCutoffDateAndTime ?? null,
                  price: result.data.price ?? null,
                  priceCurrency: result.data.priceCurrency ?? null,
                }
              : null
          } catch {
            this.dhlCutoffCapabilityByDropPointID[dropPointID] = null
          }
        })()
        this.dhlCutoffFetchPromiseByDropPointID[dropPointID] = promise
        return promise
      })

      return Promise.all(promises).then(() => undefined)
    },

    resetStore(): void {
      this.unsubscribeAllDropPoints?.()
      this.unsubscribePrivateDropPoints?.()
      this.labDropPoints = []
      this.privateDropPoints = []
      this.unsubscribeAllDropPoints = null
      this.unsubscribePrivateDropPoints = null
      this.dhlCutoffCapabilityByDropPointID = {}
      this.dhlCutoffFetchPromiseByDropPointID = {}
    },
  },
})

